import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Plus } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

const priorityColors = {
  low: "bg-zinc-100 text-zinc-700",
  medium: "bg-amber-100 text-amber-800",
  high: "bg-rose-100 text-rose-800",
};
const statusColors = {
  pending: "bg-zinc-100 text-zinc-700",
  in_progress: "bg-sky-100 text-sky-800",
  completed: "bg-emerald-100 text-emerald-800",
};

export default function Tasks() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    title: "", description: "", assigned_to: "", priority: "medium",
    deadline: new Date().toISOString().slice(0, 10),
  });

  const load = async () => {
    const { data } = await api.get("/tasks");
    setRows(data);
    if (["admin", "principal"].includes(user.role)) {
      const u = await api.get("/users").catch(() => ({ data: [] }));
      setUsers(u.data.filter((x) => ["teacher", "staff", "accounts"].includes(x.role)));
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/tasks", form);
      toast.success("Task assigned");
      setForm({ title: "", description: "", assigned_to: "", priority: "medium", deadline: new Date().toISOString().slice(0, 10) });
      setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const updateStatus = async (id, status) => {
    try { await api.patch(`/tasks/${id}`, { status }); toast.success("Updated"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const canAssign = ["admin", "principal"].includes(user.role);
  const today = new Date().toISOString().slice(0, 10);

  return (
    <div className="space-y-6" data-testid="tasks-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-black">Daily Tasks</h1>
          <p className="text-zinc-500 mt-1">Assign and monitor responsibilities across the school team.</p>
        </div>
        {canAssign && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button data-testid="add-task-btn" className="bg-indigo-700 hover:bg-indigo-800 text-white"><Plus size={16} className="mr-1" />Assign Task</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Assign Task</DialogTitle></DialogHeader>
              <form onSubmit={create} className="space-y-3">
                <div><Label>Title</Label><Input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="task-title-input" /></div>
                <div><Label>Description</Label><Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
                <div>
                  <Label>Assign to</Label>
                  <Select value={form.assigned_to} onValueChange={(v) => setForm({ ...form, assigned_to: v })}>
                    <SelectTrigger data-testid="task-assignee-select"><SelectValue placeholder="Select person" /></SelectTrigger>
                    <SelectContent>
                      {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name} · {u.role}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Priority</Label>
                    <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="low">Low</SelectItem>
                        <SelectItem value="medium">Medium</SelectItem>
                        <SelectItem value="high">High</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div><Label>Deadline</Label><Input type="date" required value={form.deadline} onChange={(e) => setForm({ ...form, deadline: e.target.value })} /></div>
                </div>
                <Button type="submit" className="w-full bg-indigo-700 hover:bg-indigo-800 text-white" data-testid="task-submit-btn">Assign</Button>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <Card className="border-zinc-200 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Title</TableHead>
              <TableHead>Assignee</TableHead>
              <TableHead>Deadline</TableHead>
              <TableHead>Priority</TableHead>
              <TableHead>Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((t) => {
              const overdue = t.status !== "completed" && t.deadline < today;
              return (
                <TableRow key={t.id} className="row-hover">
                  <TableCell>
                    <div className="font-medium">{t.title}</div>
                    {t.description && <div className="text-xs text-zinc-500 mt-0.5">{t.description}</div>}
                  </TableCell>
                  <TableCell className="text-zinc-600">{t.assignee_name}</TableCell>
                  <TableCell className={overdue ? "text-rose-700 font-semibold" : "text-zinc-600"}>
                    {t.deadline}{overdue && " · overdue"}
                  </TableCell>
                  <TableCell>
                    <span className={`text-[10px] px-2 py-1 rounded font-semibold uppercase tracking-wide ${priorityColors[t.priority]}`}>{t.priority}</span>
                  </TableCell>
                  <TableCell>
                    {t.assigned_to === user.id || canAssign ? (
                      <Select value={t.status} onValueChange={(v) => updateStatus(t.id, v)}>
                        <SelectTrigger data-testid={`task-status-${t.id}`} className="w-36 h-8"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="pending">Pending</SelectItem>
                          <SelectItem value="in_progress">In Progress</SelectItem>
                          <SelectItem value="completed">Completed</SelectItem>
                        </SelectContent>
                      </Select>
                    ) : (
                      <span className={`text-[10px] px-2 py-1 rounded font-semibold uppercase tracking-wide ${statusColors[t.status]}`}>{t.status.replace("_", " ")}</span>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
            {rows.length === 0 && (<TableRow><TableCell colSpan={5} className="text-center py-8 text-zinc-500">No tasks assigned.</TableCell></TableRow>)}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
